import type { LucideIcon } from 'lucide-react';
import { TrendingUp, Hammer, Calendar, CheckSquare2, Wrench, BadgeDollarSign, Home, Activity, Users } from 'lucide-react';

export type MobileNavItem = {
  id: string;
  label: string;
  to: string;
  icon: LucideIcon;
};

export const MOBILE_NAV_ITEMS: MobileNavItem[] = [
  { id: 'dashboard', label: 'Dashboard', to: '/', icon: Home },
  { id: 'deals', label: 'Deals', to: '/deals', icon: TrendingUp },
  { id: 'projects', label: 'Projects', to: '/projects', icon: Hammer },
  { id: 'service', label: 'Service', to: '/service-maintenance', icon: Wrench },
  { id: 'estimates', label: 'Estimates', to: '/estimates', icon: BadgeDollarSign },
  { id: 'contacts', label: 'Contacts', to: '/contacts', icon: Users },
  { id: 'tasks', label: 'Tasks', to: '/tasks', icon: CheckSquare2 },
  { id: 'activity', label: 'Activity', to: '/activities', icon: Activity },
  { id: 'tradeshows', label: 'Tradeshows', to: '/tradeshows', icon: BadgeDollarSign },
  { id: 'calendar', label: 'Calendar', to: '/calendar', icon: Calendar },
];

const MOBILE_HOME_PATH = '/';
export type MobileDestination = 'supported' | 'desktop' | 'unknown';
const supportedPaths = new Set([
  '/', '/notifications', '/activities', '/calendar', '/about', '/history', '/guides', '/privacy', '/terms',
  '/deals', '/deals/my', '/deals/solar', '/deals/electrical', '/deals/service-maintenance', '/deals/remove-and-reinstall', '/deals/new',
  '/projects', '/projects/solar', '/projects/electrical', '/projects/service-maintenance', '/projects/remove-and-reinstall',
  '/service-maintenance', '/service-maintenance/service', '/service-maintenance/maintenance',
  '/contacts', '/tasks', '/tradeshows', '/estimates',
]);
const desktopPaths = new Set(['/projects/new', '/projects/heco', '/projects/designs', '/projects/permits', '/service-maintenance/billing', '/contacts/new', '/tradeshows/new']);
const desktopRoots = new Set(['messages', 'employees', 'crews', 'planning', 'load-calcs', 'nem-amendments', 'reports', 'sops', 'procurement', 'items', 'users', 'quickbooks', 'payroll', 'feed', 'expenses', 'dev', 'decisions', 'board-advisor', 'resources', 'files', 'solar-design', 'cad', 'enphase', 'google-ads-analytics', 'playground', 'pdf-test', 'tools', 'connections', 'database']);

/** Classify actual destinations, never whole supported module prefixes. */
export function resolveMobileDestination(target: string): MobileDestination {
  if (!target) return 'supported';
  if (!target.startsWith('/') || target.startsWith('//')) return 'unknown';
  const pathname = target.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  if (supportedPaths.has(pathname)) return 'supported';
  if (desktopPaths.has(pathname)) return 'desktop';
  const segments = pathname.split('/').filter(Boolean);
  if (desktopRoots.has(segments[0])) return 'desktop';
  if (segments[0] === 'calendar' && segments.length === 2) return 'supported';
  if (['deals', 'projects', 'service-maintenance', 'contacts', 'tasks', 'tradeshows', 'estimates'].includes(segments[0])) {
    if (segments.length === 2) return 'supported';
    if (segments[0] === 'deals' && segments.length === 3 && segments[2] === 'web-proposal') return 'desktop';
  }
  return 'unknown';
}

export function mobileDestinationMessage(target: string): string {
  return resolveMobileDestination(target) === 'desktop'
    ? 'This feature is available on desktop. You can keep working here on your phone.'
    : 'This destination is unavailable. You can keep working here on your phone.';
}

export function isPathAllowedOnMobile(pathname: string): boolean {
  return resolveMobileDestination(pathname) === 'supported';
}

export function isNavItemActive(pathname: string, item: MobileNavItem): boolean {
  if (item.to === MOBILE_HOME_PATH) {
    return pathname === MOBILE_HOME_PATH;
  }

  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}
